import React, { useCallback, useState, useContext } from "react";
import { Formik, Form } from "formik";
import { ILoginProps, ILoginFormValues, ILoginResponse } from "./LoginInterfaces";
import { adminLogin, HMILogin, corporateLogin } from "../../api/queries/loginAPI";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { loginSchema } from "../../validations/schemas";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";
import { UserContext } from "../../contexts/UserContext/UserContext";
import { resetLocalStorage, setToken } from "../../utilities/storageUtilities";
import { TextField, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { useStyles } from "./Login.styles";
import {
    CORPORATE_BACKOFFICE_PATHS,
    HMI_PATHS,
    LocalStorageGlobalKeys,
    MADERA_BACKOFFICE_PATHS,
    ServerResponseErrorCodes,
    USERTYPE,
} from "constants/constants";
import useUptimeDowntimeActionsHMI from "hooks/useUptimeDowntimeActionsHMI";
import { MillOnboardingContext } from "contexts/MillOnboardingContext/MillOnboardingContext";
import { HMICachedDataContext } from "contexts/HMICachedDataContext/HMICachedDataContext";
import { ClusterCachedDataContext } from "contexts/ClusterCachedDataContext/ClusterCachedDataContext";

export const Login = ({ title, redirect }: ILoginProps) => {
    const { t } = useTranslation();
    const [loginError, setLoginError] = useState(false);
    const [loginErrorMessage, setLoginErrorMessage] = useState("");
    const { handleTokenAvailable, handleUserId } = useContext(UserContext);
    const {
        dispatch: { GetData },
    } = useContext(HMICachedDataContext);
    const {
        dispatch: { GetClusterData },
    } = useContext(ClusterCachedDataContext);
    const { handleHMILogin, handleClusterLogin } = useUptimeDowntimeActionsHMI();
    const navigate = useNavigate();
    const location = useLocation();
    const classes = useStyles();
    const pathname = location.pathname?.toLowerCase();
    const { isOnboardingFetch } = useContext(MillOnboardingContext);
    const searchParams = new URLSearchParams(location.search);
    const source = searchParams.get("source");
    const millId = searchParams.get("mill_id");

    const handleSubmit = useCallback(
        async (values: ILoginFormValues) => {
            let loginResponse;
            resetLocalStorage([LocalStorageGlobalKeys.LANGUAGES, LocalStorageGlobalKeys.I18_LANGUAGE_SELECTION]);

            if (pathname === `/${MADERA_BACKOFFICE_PATHS.ROOT_PATH}/${MADERA_BACKOFFICE_PATHS.LOGIN}`) {
                loginResponse = await adminLogin(values, handleUserId);
            }

            if (pathname.toLowerCase() === `/${HMI_PATHS.ROOT_PATH.toLowerCase()}/${HMI_PATHS.LOGIN}`) {
                loginResponse = await HMILogin(values);
            }

            if (pathname === `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.LOGIN}`) {
                loginResponse = await corporateLogin(values, handleUserId);
            }

            if (loginResponse instanceof AxiosError) {
                setLoginError(true);

                if (loginResponse.response?.status === ServerResponseErrorCodes.UNAUTHORIZED) {
                    if (pathname === `/${HMI_PATHS.ROOT_PATH.toLowerCase()}/${HMI_PATHS.LOGIN}`) {
                        setLoginErrorMessage(t("login.wrong_credentials_error_hmi"));
                    } else {
                        setLoginErrorMessage(t("login.wrong_credentials_error"));
                    }
                    return;
                }

                if (loginResponse.response?.status === ServerResponseErrorCodes.FORBIDDEN) {
                    if (pathname === `/${MADERA_BACKOFFICE_PATHS.ROOT_PATH}/${MADERA_BACKOFFICE_PATHS.LOGIN}`) {
                        setLoginErrorMessage(t("login.unauthorized_login_madera_backoffice"));
                    } else {
                        setLoginErrorMessage(t("login.unauthorized_login_HMI"));
                    }
                    return;
                }

                if (loginResponse.response?.status === ServerResponseErrorCodes.INTERNAL_SERVER_ERROR) {
                    setLoginErrorMessage(t("login.server_error"));
                    return;
                }

                if (loginResponse.response?.status === 404) {
                    setLoginErrorMessage(t("login.wrong_credentials_error"));
                    return;
                }
            }

            const { data } = loginResponse as ILoginResponse;

            if (data.token) {
                setLoginError(false);
                setLoginErrorMessage("");
                setToken(data.token);
                handleTokenAvailable(true);

                if (pathname.toLowerCase() === `/${HMI_PATHS.ROOT_PATH.toLowerCase()}/${HMI_PATHS.LOGIN}`) {
                    try {
                        const isLogin = true;
                        if (data.machine_center) {
                            await GetData(data.machine_center.machine_center_id, isLogin);
                            await handleHMILogin(data.machine_center, isLogin);
                        } else if (data.cluster) {
                            await GetClusterData(data.cluster.cluster_id, isLogin);
                            await handleClusterLogin(data.cluster.machine_centers, isLogin);
                        }
                    } catch {
                        setLoginError(true);
                        setLoginErrorMessage(t("login.data_setup_error"));
                        return;
                    }
                }

                await isOnboardingFetch();

                if (redirect) {
                    if (
                        pathname.toLowerCase() ===
                        `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH.toLowerCase()}/${CORPORATE_BACKOFFICE_PATHS.LOGIN}`
                    ) {
                        if (source) {
                            if (source === "birch_notification") {
                                navigate(
                                    `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}`,
                                );
                            } else if (source === "aspen_notification" && millId) {
                                navigate(
                                    `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.LOCATIONS.ROOT}/${millId}`,
                                );
                            }
                        } else {
                            if (
                                data.user.user_type.user_type_id === USERTYPE.ADMINISTRATOR ||
                                data.user.user_type.user_type_id === USERTYPE.EXECUTIVE
                            ) {
                                if (data.user.corporate.mills.length === 1) {
                                    navigate(
                                        `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.LOCATIONS.ROOT}/${data.user.corporate.mills[0].mill_id}`,
                                    );
                                } else {
                                    navigate(
                                        `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.CORPORATE_DASHBOARD}`,
                                    );
                                }
                            } else {
                                navigate(
                                    `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${
                                        CORPORATE_BACKOFFICE_PATHS.LOCATIONS.ROOT
                                    }/${data.user.mill_id || 0}`,
                                );
                            }
                        }
                    } else {
                        navigate(redirect);
                    }
                }
            }
        },
        [
            navigate,
            t,
            redirect,
            pathname,
            handleTokenAvailable,
            handleHMILogin,
            handleClusterLogin,
            isOnboardingFetch,
            handleUserId,
            GetClusterData,
            GetData,
            source,
            millId,
        ],
    );

    const initialValues =
        pathname === `/${HMI_PATHS.ROOT_PATH.toLowerCase()}/${HMI_PATHS.LOGIN}`
            ? { email: "", password: "", showCode: true, code: "" }
            : { email: "", password: "" };

    return (
        <Box className={classes.loginContainer}>
            <Typography variant="h3" className={classes.loginTitle}>
                {title}
            </Typography>
            <Formik initialValues={initialValues} validationSchema={loginSchema} onSubmit={handleSubmit}>
                {({ values, isSubmitting, errors, touched, setFieldValue }) => {
                    return (
                        <Form className={classes.loginForm}>
                            <Box className={classes.fieldsContainer}>
                                <TextField
                                    id="outlined-email-input"
                                    type="text"
                                    label={t("login.email")}
                                    variant="outlined"
                                    autoFocus={true}
                                    value={values.email}
                                    onChange={(e) => setFieldValue("email", e.target.value as string)}
                                    className={classes.textField}
                                    error={touched.email && Boolean(errors.email)}
                                    helperText={touched.email && `${t(errors.email!)}`}
                                />
                                <TextField
                                    id="outlined-password-input"
                                    type="password"
                                    label={t("login.password")}
                                    variant="outlined"
                                    value={values.password}
                                    onChange={(e) => setFieldValue("password", e.target.value as string)}
                                    className={classes.textField}
                                    error={touched.password && Boolean(errors.password)}
                                    helperText={touched.password && `${t(errors.password!)}`}
                                />
                                {pathname === `/${HMI_PATHS.ROOT_PATH.toLowerCase()}/${HMI_PATHS.LOGIN}` && (
                                    <TextField
                                        id="outlined-code-input"
                                        type="text"
                                        label={t("login.code")}
                                        variant="outlined"
                                        value={values.code}
                                        onChange={(e) => setFieldValue("code", e.target.value as string)}
                                        className={classes.textField}
                                        error={touched.code && Boolean(errors.code)}
                                        helperText={touched.code && `${t(errors.code!)}`}
                                    />
                                )}
                            </Box>
                            {loginError && (
                                <Box className={classes.errorMessage}>
                                    <Typography variant="error">{loginErrorMessage}</Typography>
                                </Box>
                            )}
                            <Button
                                className={classes.submitButton}
                                variant="contained"
                                type="submit"
                                disabled={isSubmitting}
                            >
                                {t("login.submit")}
                            </Button>
                            {pathname ===
                                `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.LOGIN}` && (
                                <Link
                                    className={classes.forgotPasswordLink}
                                    to={`/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.FORGOT_PASSWORD.ROOT}`}
                                >
                                    <Typography variant="h6">{t("login.forgot_password")}</Typography>
                                </Link>
                            )}
                        </Form>
                    );
                }}
            </Formik>
        </Box>
    );
};
